import React, { useMemo } from 'react';
import styled from 'styled-components';
import { VirtualizedTable } from 'core/components/table';
import QueryModel from 'app/stores/ude/QueryModel';
import { Column } from 'core/components/table/table.types';
import UdeQueryResultModel from 'app/stores/ude/UdeQueryResultModel';
import { getDimensionDisplayLabel, getDimensions } from '@kentik/ui-shared/query/query';
import { getMeasurementDimension } from '@kentik/ui-shared/query/measurements';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { startCase } from 'lodash';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { observer } from 'mobx-react';
import { RollupResult } from './util/getRollupResults';
import { renderDimensionValue, renderMetricValue } from './legendUtils';

interface Props {
  queryModel: QueryModel;
  measurement: Measurement;
  rollups: RollupResult[];
  onRowClick: (rowId: string, rowIndex: number, evt: React.MouseEvent) => void;
  onRowMouseEnter: (rowId: string) => void;
  onRowMouseLeave: () => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 100%;

  .tbody {
    .tr {
      .td {
        transition: opacity 0.2s ease;
      }

      &.isCharted:not(.isVisible),
      &.${Classes.DISABLED} {
        &:not(:hover) .td {
          opacity: 0.4;
        }
      }
    }
  }
`;

const ColorIndicator = styled.div`
  display: inline-block;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  transition: transform 0.1s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.3);
  }
`;

const SeriesColor = observer(({ model }: { model: UdeQueryResultModel }) => {
  const { queryModel } = model.results;
  const color = queryModel.getSeriesColor(model.id);
  if (!color) {
    return null;
  }
  return (
    <ColorIndicator style={{ color }}>{queryModel.isSeriesVisible(model.id) ? '\u2501' : '\u2500'}</ColorIndicator>
  );
});

function getRowClass(model: UdeQueryResultModel): string {
  const { queryModel } = model.results;
  const isTableOnly = queryModel.settings.chart.type === 'table';
  const isVisible = !isTableOnly && queryModel.isSeriesVisible(model.id);
  const isCharted = !isTableOnly && queryModel.isRowCharted(model.id);
  const isDisabled = !isTableOnly && !queryModel.isRowCharted(model.id);
  return classNames({ isTableOnly, isVisible, isCharted, [Classes.DISABLED]: isDisabled });
}

function getColumns(
  queryModel: QueryModel,
  measurement: Measurement,
  rollups: RollupResult[]
): Column<UdeQueryResultModel>[] {
  const { query, settings } = queryModel;
  const isTableOnly = settings.chart.type === 'table';
  const position = isTableOnly ? 'bottom' : settings.chart.legend?.position || 'bottom';
  const showRollups = isTableOnly || (settings?.chart?.legend?.showRollups ?? true);
  const cols: Column<UdeQueryResultModel>[] = [];

  if (!isTableOnly) {
    cols.push({
      name: 'color',
      label: '',
      width: position === 'right' ? 28 : 36,
      padding: '6px',
      ellipsis: false,
      sortable: false,
      renderer: ({ model }) => <SeriesColor model={model} />
    });
  }

  getDimensions(query).forEach((dimension) => {
    const info = getMeasurementDimension(measurement, dimension.name);
    const baseLabel = info?.Label || startCase(dimension.name);
    cols.push({
      name: `dimensions.${dimension.name}`,
      label: getDimensionDisplayLabel(dimension.name, baseLabel),
      sortable: false,
      renderer: ({ model }) => renderDimensionValue(dimension.name, model.get(), query, measurement)
    });
  });

  if (showRollups) {
    rollups.forEach((rollup) => {
      const metricLabel = rollup.metricInfo?.Label || rollup.metric;
      const rollupLabel = `${metricLabel} (${startCase(rollup.fn)})`;
      cols.push({
        name: `rollups.${rollup.key}`,
        label: rollupLabel,
        sortable: false,
        align: 'right',
        renderer: ({ value }) => renderMetricValue(rollup.metric, value as number, rollup.metricInfo, query.measurement)
      });
    });
  }

  return cols;
}

function UdeTable({ queryModel, measurement, rollups, onRowClick, onRowMouseEnter, onRowMouseLeave }: Props) {
  const { results, settings } = queryModel;
  const isTableOnly = settings.chart.type === 'table';

  const columns = useMemo(() => getColumns(queryModel, measurement, rollups), [measurement, queryModel, rollups]);

  const handleRowClick = (model: UdeQueryResultModel, evt: React.MouseEvent) => {
    if (queryModel.isRowCharted(model.id)) {
      const rowIndex = results.rows.findIndex((r) => r.id === model.id);
      onRowClick(model.id, rowIndex, evt);
    }
  };

  const handleRowMouseOver = (model: UdeQueryResultModel) => {
    onRowMouseEnter(model.id);
  };

  const handleRowMouseOut = () => {
    onRowMouseLeave();
  };

  return (
    <Wrapper>
      <VirtualizedTable
        flexed
        collection={results}
        columns={columns}
        rowHeight={30}
        rowClass={getRowClass}
        selectOnRowClick={false}
        onRowClick={!isTableOnly ? handleRowClick : undefined}
        onRowMouseOver={!isTableOnly ? handleRowMouseOver : undefined}
        onRowMouseOut={!isTableOnly ? handleRowMouseOut : undefined}
      />
    </Wrapper>
  );
}

export default UdeTable;
